import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RichTextEditor } from '../../components/RichTextEditor';
import { toast } from 'sonner';
import { X, Loader2, ArrowDownToLine, ChevronRight, ChevronDown, Upload, Save, Columns3, ImageIcon, Trash2 } from 'lucide-react';
import { recalcPricing, hasPct } from '../../lib/pricing';

const API = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u && u.startsWith('/api/') ? `${API}${u}` : u);

// Grid columns. `fill` = supports fill-down. `sub` = whether shown as a variant sub-field.
const COLUMNS = [
  { key: 'name', label: 'Title', type: 'text', w: 260, fill: true },
  { key: 'images', label: 'Images', type: 'images', w: 190 },
  { key: 'descriptionHtml', label: 'Description', type: 'richtext', w: 150 },
  { key: 'published', label: 'Published', type: 'bool', w: 96, fill: true },
  { key: 'featured', label: 'Featured', type: 'bool', w: 96, fill: true },
  { key: 'brand', label: 'Brand', type: 'text', w: 150, fill: true },
  { key: 'category', label: 'Part category', type: 'text', w: 150, fill: true },
  { key: 'collectionId', label: 'Collection', type: 'collection', w: 190, fill: true },
  { key: 'price', label: 'Price', type: 'number', w: 110, fill: true },
  { key: 'compareAtPrice', label: 'Compare-at', type: 'number', w: 120, fill: true },
  { key: 'cost', label: 'Cost / item', type: 'cost', w: 150, fill: true },
  { key: 'costPct', label: 'Cost % (below price)', type: 'costpct', w: 150, fill: true },
  { key: 'priceEnding', label: 'Price ending', type: 'ending', w: 130, fill: true },
  { key: 'sku', label: 'SKU', type: 'text', w: 130, fill: true },
  { key: 'status', label: 'Stock', type: 'stock', w: 130, fill: true },
  { key: 'barcode', label: 'Barcode', type: 'text', w: 130, fill: true },
  { key: 'weight', label: 'Weight', type: 'weight', w: 150, fill: true },
  { key: 'dimensions', label: 'Dimensions', type: 'dimensions', w: 240, fill: true },
  { key: 'tags', label: 'Tags', type: 'text', w: 170, fill: true },
  { key: 'seoTitle', label: 'SEO title', type: 'text', w: 190, fill: true },
  { key: 'seoDescription', label: 'SEO meta description', type: 'text', w: 220, fill: true },
];

const DEFAULT_VISIBLE = ['name', 'images', 'published', 'brand', 'price', 'compareAtPrice', 'cost', 'costPct', 'priceEnding', 'sku', 'status', 'descriptionHtml'];

const emptyDims = () => ({ length: '', width: '', height: '', unit: 'cm' });
const eq = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const Margin = ({ price, cost }) => {
  const p = Number(price); const c = Number(cost);
  if (cost === '' || cost == null || Number.isNaN(c) || Number.isNaN(p)) return null;
  const profit = p - c; const margin = p > 0 ? (profit / p) * 100 : 0;
  return <div className="text-[10px] text-neutral-500 mt-0.5 whitespace-nowrap">P ${profit.toFixed(0)} · <span className={margin < 0 ? 'text-red-600' : 'text-emerald-600'}>{margin.toFixed(0)}%</span></div>;
};

const BulkProductEditor = ({ open, onClose, products, collections, authFetch, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [orig, setOrig] = useState({});
  const [visible, setVisible] = useState(DEFAULT_VISIBLE);
  const [expanded, setExpanded] = useState([]);
  const [saving, setSaving] = useState(false);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [descModal, setDescModal] = useState(null); // {id}
  const [imgModal, setImgModal] = useState(null); // {id}
  const [uploadingId, setUploadingId] = useState(null);
  const [optPct, setOptPct] = useState('');
  const [optPos, setOptPos] = useState('2');

  useEffect(() => {
    if (!open) return;
    const clones = products.map((p) => recalcPricing({
      ...JSON.parse(JSON.stringify(p)),
      dimensions: p.dimensions || emptyDims(),
      variants: (p.variants || []).map((v) => ({ ...v })),
    }));
    setRows(clones);
    const m = {};
    clones.forEach((c) => { m[c.id] = JSON.parse(JSON.stringify(c)); });
    setOrig(m);
    setExpanded([]);
  }, [open, products]);

  const cols = useMemo(() => COLUMNS.filter((c) => visible.includes(c.key)), [visible]);

  // Apply a patch to one row and re-derive linked prices/costs.
  const applyRow = useCallback((id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? recalcPricing({ ...r, ...patch }) : r)));
  }, []);

  const setCell = useCallback((id, key, val) => applyRow(id, { [key]: val }), [applyRow]);

  // Setting/changing the cost % re-attaches (re-derives) all of that row's costs.
  const applyCostPct = useCallback((id, val) => {
    setRows((prev) => prev.map((r) => (r.id === id
      ? recalcPricing({ ...r, costPct: val, costManual: false, variants: r.variants ? r.variants.map((v) => ({ ...v, costManual: false })) : r.variants })
      : r)));
  }, []);

  const setVar = useCallback((id, vi, patch) => {
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      let variants = r.variants.map((v, j) => (j === vi ? { ...v, ...patch } : v));
      // Manual price edit detaches the option from the % rule; manual cost detaches cost.
      if ('price' in patch && !('upliftPct' in patch)) variants = variants.map((v, j) => (j === vi ? { ...v, upliftPct: '' } : v));
      if ('cost' in patch) variants = variants.map((v, j) => (j === vi ? { ...v, costManual: true } : v));
      return recalcPricing({ ...r, variants });
    }));
  }, []);

  const changed = useCallback((row, key) => {
    const o = orig[row.id]; if (!o) return false;
    if (key === 'weight') return !eq(row.weight, o.weight) || !eq(row.weightUnit, o.weightUnit);
    if (key === 'dimensions') return !eq(row.dimensions, o.dimensions);
    if (key === 'images') return !eq(row.images, o.images);
    return !eq(row[key], o[key]);
  }, [orig]);

  const varChanged = useCallback((row) => {
    const o = orig[row.id]; return o && !eq(row.variants, o.variants);
  }, [orig]);

  const editCount = useMemo(() => {
    let n = 0;
    rows.forEach((r) => {
      const dirty = COLUMNS.some((c) => c.key !== 'weight' && changed(r, c.key)) || changed(r, 'weight') || varChanged(r);
      if (dirty) n += 1;
    });
    return n;
  }, [rows, changed, varChanged]);

  const fillDown = (key) => {
    if (rows.length === 0) return;
    const first = rows[0];
    setRows((prev) => prev.map((r) => {
      let nr;
      if (key === 'weight') nr = { ...r, weight: first.weight, weightUnit: first.weightUnit };
      else if (key === 'dimensions') nr = { ...r, dimensions: JSON.parse(JSON.stringify(first.dimensions || emptyDims())) };
      else if (key === 'costPct') nr = { ...r, costPct: first.costPct, costManual: false, variants: r.variants ? r.variants.map((v) => ({ ...v, costManual: false })) : r.variants };
      else nr = { ...r, [key]: first[key] };
      return recalcPricing(nr);
    }));
    toast.success(`Filled "${COLUMNS.find((c) => c.key === key)?.label}" down ${rows.length} rows`);
  };

  // Largest variant count across selected rows (drives the option-position choices).
  const maxOptions = useMemo(() => rows.reduce((m, r) => Math.max(m, (r.variants || []).length), 0), [rows]);

  // One-click: apply "% above base" to a given option position (or all upgraded
  // options) across every selected product that has that option.
  const applyOptionMarkup = () => {
    if (!hasPct(optPct)) { toast.error('Enter a markup %'); return; }
    const pct = Number(optPct);
    const affects = (vs) => {
      if ((vs || []).length < 2) return false;
      if (optPos === 'all') return true;
      const idx = Number(optPos) - 1;
      return idx >= 1 && !!vs[idx];
    };
    const touched = rows.filter((r) => affects(r.variants)).length;
    if (!touched) { toast.error('No selected products have that option'); return; }
    setRows((prev) => prev.map((r) => {
      const vs = r.variants || [];
      if (!affects(vs)) return r;
      const variants = vs.map((v, i) => (i >= 1 && (optPos === 'all' || Number(optPos) - 1 === i) ? { ...v, upliftPct: pct } : v));
      return recalcPricing({ ...r, variants });
    }));
    const label = optPos === 'all' ? 'all upgraded options' : `option ${optPos}`;
    toast.success(`Applied +${pct}% to ${label} on ${touched} product(s)`);
  };

  const uploadFiles = async (id, files) => {
    if (!files || files.length === 0) return;
    setUploadingId(id);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        const res = await authFetch('/api/admin/upload', { method: 'POST', body: fd });
        if (!res.ok) throw new Error('Upload failed');
        const { url } = await res.json();
        urls.push(url);
      }
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, images: [...(r.images || []), ...urls] } : r)));
      toast.success(`${urls.length} image(s) added`);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setUploadingId(null); }
  };

  const removeImg = (id, i) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, images: r.images.filter((_, j) => j !== i) } : r)));
  const addImgUrl = (id, url) => { if (url.trim()) setRows((prev) => prev.map((r) => (r.id === id ? { ...r, images: [...(r.images || []), url.trim()] } : r))); };
  const moveImg = (id, from, to) => setRows((prev) => prev.map((r) => {
    if (r.id !== id) return r;
    const arr = [...(r.images || [])]; if (to < 0 || to >= arr.length) return r;
    const [x] = arr.splice(from, 1); arr.splice(to, 0, x); return { ...r, images: arr };
  }));

  const buildPatch = (row) => {
    const o = orig[row.id]; const patch = {};
    COLUMNS.forEach((c) => {
      if (c.key === 'weight') { if (changed(row, 'weight')) { patch.weight = row.weight === '' ? null : row.weight; patch.weightUnit = row.weightUnit || 'kg'; } return; }
      if (c.key === 'dimensions') {
        if (changed(row, 'dimensions')) {
          const d = row.dimensions || {};
          const empty = !d.length && !d.width && !d.height;
          patch.dimensions = empty ? null : { length: d.length === '' ? null : Number(d.length), width: d.width === '' ? null : Number(d.width), height: d.height === '' ? null : Number(d.height), unit: d.unit || 'cm' };
        }
        return;
      }
      if (c.key === 'images') { if (changed(row, 'images')) patch.images = row.images || []; return; }
      if (changed(row, c.key)) patch[c.key] = row[c.key];
    });
    if ((row.costManual || false) !== ((o && o.costManual) || false)) patch.costManual = !!row.costManual;
    if (varChanged(row)) patch.variants = row.variants;
    return patch;
  };

  const saveAll = async () => {
    const updates = rows.map((r) => ({ id: r.id, patch: buildPatch(r) })).filter((u) => Object.keys(u.patch).length > 0);
    if (updates.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      const res = await authFetch('/api/admin/products/bulk-update', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
      if (!res.ok) throw new Error('Save failed');
      const r = await res.json();
      toast.success(`${r.updated} product(s) updated${r.failed?.length ? ` · ${r.failed.length} failed` : ''}`);
      // refresh baseline so highlights clear
      const m = { ...orig };
      rows.forEach((row) => { m[row.id] = JSON.parse(JSON.stringify(row)); });
      setOrig(m);
      onSaved?.();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setSaving(false); }
  };

  if (!open) return null;

  const cellCls = (isChanged) => `w-full h-9 px-2 text-sm rounded-none border ${isChanged ? 'border-[#FF6B00] bg-orange-50' : 'border-neutral-200'} text-neutral-900 focus:outline-none focus:border-[#FF6B00]`;

  const renderCell = (row, col) => {
    const isCh = changed(row, col.key);
    switch (col.type) {
      case 'text':
        return <input value={row[col.key] || ''} onChange={(e) => setCell(row.id, col.key, e.target.value)} className={cellCls(isCh)} data-testid={`grid-${col.key}-${row.id}`} />;
      case 'number':
        return <input type="number" value={row[col.key] ?? ''} onChange={(e) => setCell(row.id, col.key, e.target.value === '' ? (col.key === 'price' ? 0 : null) : Number(e.target.value))} className={cellCls(isCh)} data-testid={`grid-${col.key}-${row.id}`} />;
      case 'cost':
        return (
          <div>
            <input type="number" value={row.cost ?? ''} onChange={(e) => applyRow(row.id, { cost: e.target.value === '' ? null : Number(e.target.value), costManual: true })} className={cellCls(isCh)} data-testid={`grid-cost-${row.id}`} />
            <Margin price={row.price} cost={row.cost} />
          </div>
        );
      case 'costpct':
        return (
          <input type="number" value={row.costPct ?? ''} onChange={(e) => applyCostPct(row.id, e.target.value === '' ? '' : Number(e.target.value))} placeholder="% off price" className={cellCls(isCh)} data-testid={`grid-costpct-${row.id}`} />
        );
      case 'ending':
        return (
          <Select value={row.priceEnding || 'exact'} onValueChange={(v) => setCell(row.id, 'priceEnding', v)}>
            <SelectTrigger className={cellCls(isCh) + ' justify-between'} data-testid={`grid-ending-${row.id}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="exact">Exact</SelectItem><SelectItem value="99">.99</SelectItem><SelectItem value="95">.95</SelectItem></SelectContent>
          </Select>
        );
      case 'bool':
        return <div className="flex justify-center py-1"><Switch checked={!!row[col.key]} onCheckedChange={(v) => setCell(row.id, col.key, v)} className={`data-[state=checked]:bg-[#FF6B00] ${isCh ? 'ring-2 ring-[#FF6B00] rounded-full' : ''}`} data-testid={`grid-${col.key}-${row.id}`} /></div>;
      case 'collection':
        return (
          <Select value={row.collectionId || ''} onValueChange={(v) => setCell(row.id, 'collectionId', v)}>
            <SelectTrigger className={cellCls(isCh) + ' justify-between'} data-testid={`grid-collection-${row.id}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent className="max-h-72">{collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        );
      case 'stock':
        return (
          <Select value={row.status || 'in-stock'} onValueChange={(v) => setCell(row.id, 'status', v)}>
            <SelectTrigger className={cellCls(isCh) + ' justify-between'} data-testid={`grid-status-${row.id}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="in-stock">In stock</SelectItem><SelectItem value="out-of-stock">Out of stock</SelectItem></SelectContent>
          </Select>
        );
      case 'weight':
        return (
          <div className="flex gap-1">
            <input type="number" value={row.weight ?? ''} onChange={(e) => setCell(row.id, 'weight', e.target.value === '' ? '' : Number(e.target.value))} className={cellCls(isCh)} data-testid={`grid-weight-${row.id}`} />
            <Select value={row.weightUnit || 'kg'} onValueChange={(v) => setCell(row.id, 'weightUnit', v)}>
              <SelectTrigger className={`h-9 w-16 rounded-none border ${isCh ? 'border-[#FF6B00] bg-orange-50' : 'border-neutral-200'} text-neutral-900 text-xs px-1`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="kg">kg</SelectItem><SelectItem value="g">g</SelectItem><SelectItem value="lb">lb</SelectItem><SelectItem value="oz">oz</SelectItem></SelectContent>
            </Select>
          </div>
        );
      case 'dimensions': {
        const d = row.dimensions || emptyDims();
        const set = (k, v) => setCell(row.id, 'dimensions', { ...d, [k]: v });
        const dc = `w-full h-9 px-1.5 text-sm rounded-none border ${isCh ? 'border-[#FF6B00] bg-orange-50' : 'border-neutral-200'} text-neutral-900 text-center focus:outline-none focus:border-[#FF6B00]`;
        return (
          <div className="flex gap-1 items-center">
            <input type="number" placeholder="L" value={d.length ?? ''} onChange={(e) => set('length', e.target.value)} className={dc} data-testid={`grid-dim-l-${row.id}`} />
            <span className="text-neutral-400 text-xs">×</span>
            <input type="number" placeholder="W" value={d.width ?? ''} onChange={(e) => set('width', e.target.value)} className={dc} data-testid={`grid-dim-w-${row.id}`} />
            <span className="text-neutral-400 text-xs">×</span>
            <input type="number" placeholder="H" value={d.height ?? ''} onChange={(e) => set('height', e.target.value)} className={dc} data-testid={`grid-dim-h-${row.id}`} />
            <Select value={d.unit || 'cm'} onValueChange={(v) => set('unit', v)}>
              <SelectTrigger className={`h-9 w-14 rounded-none border ${isCh ? 'border-[#FF6B00] bg-orange-50' : 'border-neutral-200'} text-neutral-900 text-xs px-1`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="cm">cm</SelectItem><SelectItem value="in">in</SelectItem></SelectContent>
            </Select>
          </div>
        );
      }
      case 'images':
        return (
          <div className="flex items-center gap-1">
            <div className="flex gap-0.5">
              {(row.images || []).slice(0, 3).map((im, i) => <img key={i} src={imgSrc(im)} alt="" className="h-8 w-8 object-cover bg-neutral-100" />)}
              {(row.images || []).length === 0 && <div className="h-8 w-8 bg-neutral-100 flex items-center justify-center text-neutral-300"><ImageIcon size={14} /></div>}
            </div>
            {(row.images || []).length > 3 && <span className="text-[10px] text-neutral-500">+{row.images.length - 3}</span>}
            <button onClick={() => setImgModal({ id: row.id })} className={`ml-auto text-[10px] font-display tracking-[0.1em] px-2 py-1 border ${isCh ? 'border-[#FF6B00] text-[#FF6B00]' : 'border-neutral-300 text-neutral-600'} hover:border-[#FF6B00] hover:text-[#FF6B00]`} data-testid={`grid-images-edit-${row.id}`}>EDIT</button>
          </div>
        );
      case 'richtext': {
        const hasText = (row.descriptionHtml || '').replace(/<[^>]*>/g, '').trim().length > 0;
        return (
          <button onClick={() => setDescModal({ id: row.id })} className={`w-full h-9 px-2 text-xs text-left truncate border ${isCh ? 'border-[#FF6B00] bg-orange-50 text-[#FF6B00]' : 'border-neutral-200 text-neutral-600'} hover:border-[#FF6B00]`} data-testid={`grid-desc-edit-${row.id}`}>
            {hasText ? 'Edit description…' : 'Add description…'}
          </button>
        );
      }
      default:
        return null;
    }
  };

  const descRow = descModal ? rows.find((r) => r.id === descModal.id) : null;
  const imgRow = imgModal ? rows.find((r) => r.id === imgModal.id) : null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="bulk-editor">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-neutral-200 bg-black text-white flex-shrink-0">
        <div className="font-display tracking-[0.18em] text-sm">BULK PRODUCT EDITOR</div>
        <span className="text-xs text-white/60">{rows.length} products · {editCount} edited</span>
        <div className="ml-auto flex items-center gap-2 relative">
          <Button variant="ghost" onClick={() => setColPickerOpen((o) => !o)} className="h-9 text-white/80 hover:text-white hover:bg-white/10 rounded-none gap-2 text-xs font-display tracking-[0.12em]" data-testid="bulk-columns-btn"><Columns3 size={15} /> COLUMNS</Button>
          {colPickerOpen && (
            <div className="absolute right-0 top-11 z-50 w-64 max-h-96 overflow-y-auto bg-white text-neutral-900 border border-neutral-200 shadow-xl p-3" data-testid="bulk-columns-panel">
              <div className="text-[11px] font-display tracking-[0.15em] text-neutral-500 mb-2">SHOW COLUMNS</div>
              {COLUMNS.map((c) => (
                <label key={c.key} className="flex items-center gap-2 py-1.5 cursor-pointer text-sm">
                  <Checkbox checked={visible.includes(c.key)} onCheckedChange={(v) => setVisible((prev) => (v ? [...prev, c.key] : prev.filter((k) => k !== c.key)))} data-testid={`bulk-col-${c.key}`} />
                  {c.label}
                </label>
              ))}
            </div>
          )}
          <Button onClick={saveAll} disabled={saving || editCount === 0} className="h-9 rounded-none bg-[#FF6B00] hover:bg-[#ff8534] text-black font-display tracking-[0.12em] text-xs gap-2" data-testid="bulk-save-all">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} SAVE ALL ({editCount})
          </Button>
          <button onClick={onClose} className="h-9 w-9 flex items-center justify-center text-white/70 hover:text-white" data-testid="bulk-close"><X size={18} /></button>
        </div>
      </div>

      {/* Option-markup toolbar */}
      {maxOptions >= 2 && (
        <div className="flex items-center gap-2 px-6 py-2 border-b border-neutral-200 bg-neutral-50 flex-shrink-0 flex-wrap" data-testid="bulk-optmarkup-bar">
          <span className="text-[11px] font-display tracking-[0.14em] text-neutral-500">OPTION MARKUP</span>
          <span className="text-xs text-neutral-500">Set</span>
          <div className="flex items-center">
            <span className="text-sm text-neutral-500 mr-0.5">+</span>
            <input type="number" value={optPct} onChange={(e) => setOptPct(e.target.value)} placeholder="%" className="h-8 w-16 px-2 text-sm rounded-none border border-neutral-300 text-neutral-900 focus:outline-none focus:border-[#FF6B00]" data-testid="bulk-optmarkup-pct" />
            <span className="text-sm text-neutral-500 ml-0.5">% above base on</span>
          </div>
          <Select value={optPos} onValueChange={setOptPos}>
            <SelectTrigger className="h-8 w-40 rounded-none border-neutral-300 text-neutral-900 text-sm" data-testid="bulk-optmarkup-pos"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: Math.max(0, maxOptions - 1) }, (_, k) => k + 2).map((pos) => (
                <SelectItem key={pos} value={String(pos)}>{`Option ${pos}`}{pos === 2 ? ' (2nd)' : ''}</SelectItem>
              ))}
              <SelectItem value="all">All upgraded options</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-neutral-500">for all selected products</span>
          <Button onClick={applyOptionMarkup} className="h-8 rounded-none bg-black hover:bg-[#FF6B00] text-white hover:text-black font-display tracking-[0.12em] text-xs" data-testid="bulk-optmarkup-apply">APPLY</Button>
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-auto" onClick={() => colPickerOpen && setColPickerOpen(false)}>
        <table className="border-collapse" style={{ minWidth: 'max-content' }}>
          <thead className="sticky top-0 z-20">
            <tr className="bg-neutral-100">
              <th className="sticky left-0 z-30 bg-neutral-100 border-b border-r border-neutral-200 px-2 py-2 w-10" />
              {cols.map((c) => (
                <th key={c.key} className="border-b border-r border-neutral-200 px-2 py-2 text-left align-middle" style={{ minWidth: c.w }}>
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] font-display tracking-[0.12em] text-neutral-700 whitespace-nowrap">{c.label}</span>
                    {c.fill && rows.length > 1 && (
                      <button onClick={() => fillDown(c.key)} title="Fill this value down to all rows" className="text-neutral-400 hover:text-[#FF6B00]" data-testid={`bulk-filldown-${c.key}`}><ArrowDownToLine size={12} /></button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const isExp = expanded.includes(row.id);
              const hasVars = (row.variants || []).length > 0;
              return (
                <React.Fragment key={row.id}>
                  <tr className="hover:bg-neutral-50" data-testid={`bulk-row-${row.id}`}>
                    <td className="sticky left-0 z-10 bg-white border-b border-r border-neutral-200 px-1 text-center align-top pt-2">
                      <div className="text-[10px] text-neutral-400">{ri + 1}</div>
                      {hasVars && (
                        <button onClick={() => setExpanded((p) => (p.includes(row.id) ? p.filter((x) => x !== row.id) : [...p, row.id]))} className="text-neutral-500 hover:text-[#FF6B00]" data-testid={`bulk-expand-${row.id}`}>
                          {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      )}
                    </td>
                    {cols.map((c) => (
                      <td key={c.key} className="border-b border-r border-neutral-100 px-1.5 py-1 align-top" style={{ minWidth: c.w }}>
                        {renderCell(row, c)}
                      </td>
                    ))}
                  </tr>
                  {isExp && hasVars && (row.variants || []).map((v, vi) => (
                    <tr key={`${row.id}-v${vi}`} className="bg-neutral-50/70" data-testid={`bulk-varrow-${row.id}-${vi}`}>
                      <td className="sticky left-0 z-10 bg-neutral-50 border-b border-r border-neutral-200" />
                      <td colSpan={cols.length} className="border-b border-neutral-100 px-3 py-2">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-[10px] font-display tracking-[0.15em] text-neutral-400">OPTION{vi === 0 ? ' · BASE' : ''}</span>
                          <span className="text-sm text-neutral-800 font-medium min-w-[120px]">{v.option || `Option ${vi + 1}`}</span>
                          {vi === 0 ? (
                            <span className="text-[10px] text-neutral-400 italic">base price — reference for % uplifts</span>
                          ) : (
                            <label className="text-[10px] text-neutral-500">% above base
                              <input type="number" value={v.upliftPct ?? ''} onChange={(e) => setVar(row.id, vi, { upliftPct: e.target.value === '' ? '' : Number(e.target.value) })} placeholder="e.g. 30" className="ml-1 h-8 w-20 px-2 text-sm border border-neutral-200 rounded-none text-neutral-900" data-testid={`bulk-var-uplift-${row.id}-${vi}`} />
                              {hasPct(v.upliftPct) && <span className="ml-1 text-emerald-600">auto</span>}
                            </label>
                          )}
                          <label className="text-[10px] text-neutral-500">Price
                            <input type="number" value={v.price ?? ''} onChange={(e) => setVar(row.id, vi, { price: e.target.value === '' ? null : Number(e.target.value) })} className="ml-1 h-8 w-24 px-2 text-sm border border-neutral-200 rounded-none text-neutral-900" data-testid={`bulk-var-price-${row.id}-${vi}`} />
                          </label>
                          <label className="text-[10px] text-neutral-500">Cost
                            <input type="number" value={v.cost ?? ''} onChange={(e) => setVar(row.id, vi, { cost: e.target.value === '' ? null : Number(e.target.value) })} className="ml-1 h-8 w-24 px-2 text-sm border border-neutral-200 rounded-none text-neutral-900" data-testid={`bulk-var-cost-${row.id}-${vi}`} />
                          </label>
                          <Margin price={v.price} cost={v.cost} />
                          <label className="text-[10px] text-neutral-500">SKU
                            <input value={v.sku || ''} onChange={(e) => setVar(row.id, vi, { sku: e.target.value })} className="ml-1 h-8 w-32 px-2 text-sm border border-neutral-200 rounded-none text-neutral-900" data-testid={`bulk-var-sku-${row.id}-${vi}`} />
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Description modal */}
      {descRow && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-6" onClick={() => setDescModal(null)}>
          <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="bulk-desc-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display tracking-[0.15em] text-neutral-900 text-sm truncate pr-4">DESCRIPTION · {descRow.name}</div>
              <button onClick={() => setDescModal(null)} className="text-neutral-400 hover:text-neutral-900"><X size={18} /></button>
            </div>
            <div className="[&_.ql-editor]:text-neutral-900">
              <RichTextEditor value={descRow.descriptionHtml || ''} onChange={(html) => setCell(descRow.id, 'descriptionHtml', html)} placeholder="Describe the product…" testId="bulk-desc-editor" />
            </div>
            <Button onClick={() => setDescModal(null)} className="mt-4 h-10 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.12em] text-xs" data-testid="bulk-desc-done">DONE</Button>
          </div>
        </div>
      )}

      {/* Images modal */}
      {imgRow && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-6" onClick={() => setImgModal(null)}>
          <div className="bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="bulk-img-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display tracking-[0.15em] text-neutral-900 text-sm truncate pr-4">IMAGES · {imgRow.name}</div>
              <button onClick={() => setImgModal(null)} className="text-neutral-400 hover:text-neutral-900"><X size={18} /></button>
            </div>
            <p className="text-[11px] text-neutral-500 mb-3">Drag thumbnails to reorder — the first image shows on cards & listings.</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {(imgRow.images || []).map((im, i) => (
                <div key={i} draggable
                  onDragStart={(e) => e.dataTransfer.setData('from', String(i))}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); moveImg(imgRow.id, Number(e.dataTransfer.getData('from')), i); }}
                  className="relative h-20 w-20 bg-neutral-100 cursor-move group border border-neutral-200" data-testid={`bulk-img-${imgRow.id}-${i}`}>
                  <img src={imgSrc(im)} alt="" className="h-full w-full object-cover" />
                  {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[8px] text-center tracking-[0.1em] py-0.5">MAIN</span>}
                  <button onClick={() => removeImg(imgRow.id, i)} className="absolute -top-1.5 -right-1.5 bg-black text-white rounded-full p-0.5" data-testid={`bulk-img-remove-${imgRow.id}-${i}`}><X size={11} /></button>
                </div>
              ))}
              {(imgRow.images || []).length === 0 && <div className="text-xs text-neutral-400 py-6">No images yet.</div>}
            </div>
            <div className="flex gap-2">
              <label className="flex items-center gap-2 px-3 h-9 border border-neutral-300 cursor-pointer text-xs font-display tracking-[0.12em] hover:border-[#FF6B00]" data-testid={`bulk-img-upload-${imgRow.id}`}>
                {uploadingId === imgRow.id ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} UPLOAD
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadFiles(imgRow.id, Array.from(e.target.files || [])); e.target.value = ''; }} />
              </label>
              <Input placeholder="…or paste image URL, press Enter" className="rounded-none border-neutral-300 h-9 text-sm text-neutral-900"
                onKeyDown={(e) => { if (e.key === 'Enter') { addImgUrl(imgRow.id, e.target.value); e.target.value = ''; } }} data-testid={`bulk-img-url-${imgRow.id}`} />
            </div>
            <Button onClick={() => setImgModal(null)} className="mt-4 h-10 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.12em] text-xs" data-testid="bulk-img-done">DONE</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkProductEditor;
